import { Box } from '@mui/material';

export interface ProgressRingProps {
  value: number;
  color: string;
  trackColor: string;
  size?: number;
}

const ProgressRing = ({
  value,
  color,
  trackColor,
  size = 30,
}: ProgressRingProps) => {
  const progress = Number.isFinite(value)
    ? Math.min(100, Math.max(0, value))
    : 0;
  const stroke = size / 6;
  // Half a unit keeps the stroke inside the viewBox.
  const radius = (size - stroke) / 2 - 0.5;
  const center = size / 2;
  const circumference = 2 * Math.PI * radius;
  const full = progress >= 100;
  const cap = stroke / 2;
  // Round caps add one stroke. The start moves forward by half of that, and the
  // dash is shorter by the whole stroke, so the arc runs from 12 o'clock to the percent.
  // A value smaller than the stroke still draws a sliver.
  const dash =
    progress > 0
      ? Math.max((progress / 100) * circumference - stroke, 0.01)
      : 0;
  const startAngle = -90 + (cap / circumference) * 360;

  return (
    <Box
      component="svg"
      viewBox={`0 0 ${size} ${size}`}
      role="progressbar"
      aria-valuenow={Math.round(progress)}
      aria-valuemin={0}
      aria-valuemax={100}
      sx={{ width: size, height: size, display: 'block', flexShrink: 0 }}
    >
      <Box component="g" sx={{ color: trackColor }}>
        <circle
          cx={center}
          cy={center}
          r={radius}
          fill="none"
          stroke="currentColor"
          strokeWidth={stroke}
        />
      </Box>
      {progress > 0 && (
        <Box component="g" sx={{ color }}>
          <circle
            cx={center}
            cy={center}
            r={radius}
            fill="none"
            stroke="currentColor"
            strokeWidth={stroke}
            // A full ring is a plain circle. Round caps would overlap at the join.
            strokeLinecap={full ? 'butt' : 'round'}
            strokeDasharray={full ? undefined : `${dash} ${circumference}`}
            transform={
              full ? undefined : `rotate(${startAngle} ${center} ${center})`
            }
          />
        </Box>
      )}
    </Box>
  );
};

export default ProgressRing;
